import React, { useEffect, useState } from 'react';
import { useLeague } from '@shootout-comps/core';
import type { PublicLeagueSummary } from '@shootout-comps/core';
import { useLang } from '@shootout-comps/i18n';
import { SnookerBallSpinner } from '../../components/SnookerBall';

interface PublicLeaguesPageProps {
  onSelectLeague: (id: string) => void;
  onBack: () => void;
}

// Card list of public leagues — copy-adapted from PublicTournamentsPage.tsx, simplified since
// leagues have no status-color/format-badge equivalent worth surfacing at list level.
export default function PublicLeaguesPage({ onSelectLeague, onBack }: PublicLeaguesPageProps) {
  const { fetchPublicLeagues } = useLeague();
  const { t } = useLang();
  const [leagues, setLeagues] = useState<PublicLeagueSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchPublicLeagues().then(setLeagues).catch((e: Error) => setError(e.message));
  }, [fetchPublicLeagues]);

  return (
    <div style={{ maxWidth: '720px', margin: '0 auto', padding: 'var(--page-pad)' }}>
      <button onClick={onBack} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', color: 'var(--chalk-dim)', padding: '0.35rem 0.75rem', fontSize: '0.8rem', cursor: 'pointer', marginBottom: '1.25rem' }}>
        ← {t.navLeagues}
      </button>

      <h2 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(1.8rem,6vw,2.5rem)', color: 'var(--gold)', marginBottom: '0.3rem' }}>
        {t.publicLeaguesTitle}
      </h2>
      <p style={{ color: 'var(--chalk-dim)', fontSize: '0.85rem', marginBottom: '1.75rem', lineHeight: 1.6 }}>
        {t.publicLeaguesDesc}
      </p>

      {error && (
        <div style={{ background: 'rgba(192,57,43,0.15)', border: '1px solid var(--red)', borderRadius: '8px', padding: '1rem', color: '#e74c3c', marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {leagues === null && !error && (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--chalk-dim)' }}>
          <SnookerBallSpinner size={32} />
        </div>
      )}

      {leagues && leagues.length === 0 && (
        <div style={{ textAlign: 'center', color: 'var(--chalk-dim)', padding: '2.5rem', background: 'rgba(255,255,255,0.03)', borderRadius: '12px', border: '2px dashed rgba(255,255,255,0.1)' }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🌐</div>
          <p style={{ fontSize: '0.88rem' }}>{t.noPublicLeaguesYet}</p>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
        {(leagues ?? []).map((l, i) => {
          const teamCount = l.divisions.reduce((s, d) => s + d.teams.length, 0);
          return (
            <div key={l._id} onClick={() => onSelectLeague(l._id)}
              style={{ display: 'flex', alignItems: 'center', gap: '1rem', background: 'linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '1rem 1.15rem', cursor: 'pointer', transition: 'all 0.15s', animation: `fadeInPublicLeague 0.3s ease ${i * 0.04}s both` }}
              className="public-league-row"
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: '0.95rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.name}</div>
                <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.3rem', flexWrap: 'wrap' }}>
                  {l.ownerUsername && <span style={{ fontSize: '0.75rem', color: 'var(--chalk-dim)' }}>{t.hostedByLabel} {l.ownerUsername}</span>}
                  <span style={{ fontSize: '0.75rem', color: 'var(--chalk-dim)' }}>{l.divisions.length} division{l.divisions.length === 1 ? '' : 's'} · {teamCount} team{teamCount === 1 ? '' : 's'}</span>
                </div>
              </div>
              <span style={{ color: 'var(--chalk-dim)', fontSize: '1rem' }}>›</span>
            </div>
          );
        })}
      </div>

      <style>{`.public-league-row:hover{transform:translateY(-1px);box-shadow:0 4px 16px rgba(0,0,0,0.3)} @keyframes fadeInPublicLeague{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  );
}
